import { Localization, enUS as materialEnUS, frFR as materialFrFR } from '@mui/material/locale';
import { GridLocaleText } from '@mui/x-data-grid';
import { enUS as gridEnUS, frFR as gridFrFR } from '@mui/x-data-grid/locales';

import { IDesignToken } from '@emburse/ui-core';

import { Locale, getDateFnsLocale } from '@Shared/helpers/abacusTimeFormatter';
import { SupportedLocale, resolveSupportedLocale } from '@Shared/i18n/supportedLocales';

export const SIDEBAR_WIDTH = 260;

/**
 * The MUI locale bundles that render a single Emburse Spend locale:
 *   - `material`: `@mui/material/locale` bundle (component strings), applied as
 *     `createTheme`'s 2nd argument;
 *   - `gridLocaleText`: DataGrid `localeText` from `@mui/x-data-grid/locales`.
 *
 * The `date-fns` locale for the date-picker adapter is resolved separately via
 * `abacusTimeFormatter.getDateFnsLocale` so all `date-fns` access stays in one place.
 */
interface IMuiLocaleBundle {
  material: Localization;
  gridLocaleText: Partial<GridLocaleText>;
}

/**
 * Maps each supported Emburse Spend locale to its MUI locale bundles.
 *
 * MUI ships no Canadian-French (`fr-CA`) component or DataGrid catalog, so `fr-CA`
 * reuses European French (`frFR`) for those strings. English is MUI's default, so
 * the `en-US` material bundle is effectively empty.
 */
const MUI_LOCALE_BUNDLES: Record<SupportedLocale, IMuiLocaleBundle> = {
  'en-US': {
    material: materialEnUS,
    gridLocaleText: gridEnUS.components.MuiDataGrid.defaultProps.localeText
  },
  'fr-CA': {
    material: materialFrFR,
    gridLocaleText: gridFrFR.components.MuiDataGrid.defaultProps.localeText
  }
};
/**
 * Centralized theme provider for all components that use EmburseThemeProvider
 * This class should convert the Abacus style config to Emburse format and pass it off to the provider
 *
 * @export
 * @class AbacusThemeProvider
 */
class AbacusThemeProvider {
  /**
   * Returns Abacus Theme Options
   *
   * @static
   * @return {*}
   * @memberof AbacusThemeProvider
   */
  static getThemeOptions(): IDesignToken {
    const { whitelabelConfiguration } = window;
    return {
      palette: {
        monochrome: {
          main: whitelabelConfiguration?.tags?.secondaryColorCode
        },
        primary: {
          main: whitelabelConfiguration?.tags?.primaryColorCode
        },
        secondary: {
          main: whitelabelConfiguration?.tags?.secondaryColorCode
        }
      },
      sizes: {
        sidebar: {
          width: SIDEBAR_WIDTH
        }
      },
      breakpoints: {
        values: {
          xs: 0,
          sm: 600,
          md: 900,
          lg: 1180,
          xl: 1536
        }
      }
    };
  }

  /**
   * Returns the active `@mui/material/locale` bundle to pass as `createTheme`'s
   * second argument so MUI component strings render in the active locale. The
   * active locale is read from `window.LOCALE` (injected by the server bootstrap)
   * and resolved against the locales Emburse Spend supports, defaulting to `en-US`.
   *
   * @static
   * @memberof AbacusThemeProvider
   */
  static getMuiMaterialLocale(): Localization {
    return MUI_LOCALE_BUNDLES[resolveSupportedLocale(window.LOCALE)].material;
  }

  /**
   * Returns the active `date-fns` locale for the date-picker adapter, passed as
   * `LocalizationProvider`'s `adapterLocale`. Delegates to `abacusTimeFormatter`
   * so all `date-fns` locale access lives in one place.
   *
   * @static
   * @memberof AbacusThemeProvider
   */
  static getDateFnsLocale(): Locale {
    return getDateFnsLocale(window.LOCALE);
  }

  /**
   * Returns the active DataGrid `localeText` (from `@mui/x-data-grid/locales`)
   * for the `ServerDataGrid` wrapper.
   *
   * @static
   * @memberof AbacusThemeProvider
   */
  static getDataGridLocaleText(): Partial<GridLocaleText> {
    return MUI_LOCALE_BUNDLES[resolveSupportedLocale(window.LOCALE)].gridLocaleText;
  }
}

export { AbacusThemeProvider };
